import { useId } from 'react'

import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useParametrosProposta } from '@/features/parametros/hooks/use-parametros-proposta'
import { ImobiliariaCorretorStep } from '@/features/proposta/components/imobiliaria-corretor-step'
import { TermosObservacoesDaPropostaStep } from '@/features/proposta/components/termos-da-proposta-step'
import { CardEtapaContent, CardEtapaItem, CardsEtapas } from '@/features/proposta/components/ui/card-etapa'
import { obterTodosCompradoresPFEPJ } from '@/features/proposta/entities/Comprador/merge-comprador-pj-pf'
import { useStepValidationStrategy } from '@/features/proposta/hooks'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderDescription,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/layouts'
import type { TPropostaSchemaInput } from '@/features/proposta/schemas'
import { ParticipacaoCompradorContent } from '@/pages/proposta/etapa-detalhes-da-proposta/participacao-comprador-content'
import { Render } from '@/shared/components/render-if'
import { Button, Label } from '@/shared/components/ui'
import { FormField } from '@/shared/components/ui/form'
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group'

export const DetalhesDaPropostaStep = () => {
  const { t } = useTranslation('proposta')
  const { control, watch, setValue, trigger } = useFormContext<TPropostaSchemaInput>()
  const { data: parametros } = useParametrosProposta()
  const { validateStep } = useStepValidationStrategy(trigger)
  const id = useId()

  const assinaturaContrato = watch('detalhesDaProposta.assinaturaContrato')
  const currentStep = watch('step')

  const assinaturaMap = {
    Eletronica: t('detalhes_proposta.assinaturas.options.eletronica'),
    Impressa: t('detalhes_proposta.assinaturas.options.impressa'),
  }

  const handleSelecionarTipoAssinatura = () => {
    if (!watch('detalhesDaProposta.termosDaProposta')) return
    setValue('detalhesDaProposta.termosDaProposta', '')
  }

  const compradores = obterTodosCompradoresPFEPJ(
    watch('cadastroCompradores.compradores'),
    watch('cadastroCompradores.clientes'),
    watch('cadastroCompradores.clientesJuridicos'),
  )

  const handlePrevStep = () => {
    setValue('step', currentStep - 1)
  }

  const handleNextStep = async () => {
    const isValid = await validateStep('DetalhesDaPropostaStep')
    if (!isValid) return
    setValue('step', currentStep + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>{t('detalhes_proposta.titulo')}</StepPropostaHeaderTitle>
        <StepPropostaHeaderDescription>{t('detalhes_proposta.subtitulo')}</StepPropostaHeaderDescription>
      </StepPropostaHeaderContent>
      <StepPropostaContent>
        <CardsEtapas type="multiple">
          <Render.If condition={compradores.length > 1}>
            {() => <ParticipacaoCompradorContent compradores={compradores} />}
          </Render.If>
          <ImobiliariaCorretorStep
            percentualCorretagem={parametros?.percentualCorretagem ?? null}
            valorCorretagem={parametros?.valorCorretagem ?? null}
            exibeFormaPagamentoComissao={parametros?.exibeFormaPagamentoComissao}
          />
          <CardEtapaItem
            titulo={t('detalhes_proposta.assinaturas.titulo')}
            descricao={t('detalhes_proposta.assinaturas.descricao')}
            completed={Boolean(watch(`detalhesDaProposta.assinaturaContrato`))}
          >
            <CardEtapaContent>
              <span className="text-sm font-medium text-neutral-800">
                {t('detalhes_proposta.assinaturas.modelo_assinatura')}
              </span>
              <FormField
                name="detalhesDaProposta.assinaturaContrato"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    className="flex flex-col gap-6"
                    value={field.value}
                    onValueChange={(v) => {
                      field.onChange(v)
                      handleSelecionarTipoAssinatura()
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="Eletronica" id={`${id}-Eletronica`} />
                      <Label className="font-normal" htmlFor={`${id}-Eletronica`}>
                        {assinaturaMap.Eletronica}
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="Impressa" id={`${id}-Impressa`} />
                      <Label className="font-normal" htmlFor={`${id}-Impressa`}>
                        {assinaturaMap.Impressa}
                      </Label>
                    </div>
                  </RadioGroup>
                )}
              />
            </CardEtapaContent>
          </CardEtapaItem>
          <TermosObservacoesDaPropostaStep
            assinaturaContrato={assinaturaContrato ? assinaturaMap[assinaturaContrato] : ''}
            termosDaProposta={parametros?.termosDaProposta ?? ''}
          />
        </CardsEtapas>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button className="font-nunito h-12" variant="outline" onClick={handlePrevStep}>
          {t('etapa_anterior')}
        </Button>
        <Button className="font-nunito h-12" variant="default" onClick={handleNextStep}>
          {t('proxima_etapa')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
